import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface NotificationSettings {
  adminEmails: string;
  notifyAdmin: boolean;
  notifyInstructor: boolean;
  sendReceipt: boolean;
}

const DEFAULTS: NotificationSettings = {
  adminEmails: "silversagehomeschoolhubs@gmail.com",
  notifyAdmin: true,
  notifyInstructor: true,
  sendReceipt: true,
};

/**
 * Who hears about a new registration. Every switch here is read by the backend
 * fan-out that runs after a payment commits — nothing is hardcoded in the
 * email functions.
 */
export default function RegistrationNotificationsCard() {
  const [value, setValue] = useState<NotificationSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await (supabase as any)
        .from("business_settings")
        .select(
          "admin_notification_emails, notify_admin_on_registration, notify_instructor_on_registration, send_receipt_email",
        )
        .eq("id", true)
        .maybeSingle();
      if (data) {
        setValue({
          adminEmails: Array.isArray(data.admin_notification_emails)
            ? data.admin_notification_emails.join(", ")
            : DEFAULTS.adminEmails,
          notifyAdmin: data.notify_admin_on_registration !== false,
          notifyInstructor: data.notify_instructor_on_registration !== false,
          sendReceipt: data.send_receipt_email !== false,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const emails = value.adminEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    const bad = emails.find((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) {
      toast({
        title: "That email address doesn't look right",
        description: `"${bad}" is missing something. Separate multiple addresses with commas.`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any).from("business_settings").upsert(
        {
          id: true,
          admin_notification_emails: emails,
          notify_admin_on_registration: value.notifyAdmin,
          notify_instructor_on_registration: value.notifyInstructor,
          send_receipt_email: value.sendReceipt,
        },
        { onConflict: "id" },
      );
      if (error) throw error;
      toast({ title: "Saved", description: "New registrations will follow these rules." });
    } catch (e: any) {
      toast({
        title: "We couldn't save that",
        description: e?.message ?? "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4" /> Registration notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Sent automatically after a payment settles — never when checkout merely starts. Every send
          is logged with its delivery status.
        </p>

        <div className="space-y-2">
          <Label htmlFor="admin-emails">Send me a copy at</Label>
          <Input
            id="admin-emails"
            value={value.adminEmails}
            disabled={loading}
            onChange={(e) => setValue((v) => ({ ...v, adminEmails: e.target.value }))}
            placeholder="you@example.com, partner@example.com"
          />
          <p className="text-xs text-muted-foreground">Separate multiple addresses with commas.</p>
        </div>

        <div className="space-y-3">
          {(
            [
              ["notifyAdmin", "Email me every new registration"],
              ["notifyInstructor", "Email the instructor when a student enrolls"],
              ["sendReceipt", "Send families a payment receipt"],
            ] as Array<[keyof NotificationSettings, string]>
          ).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <Label htmlFor={`switch-${key}`} className="font-normal">
                {label}
              </Label>
              <Switch
                id={`switch-${key}`}
                disabled={loading}
                checked={Boolean(value[key])}
                onCheckedChange={(checked) => setValue((v) => ({ ...v, [key]: checked }))}
              />
            </div>
          ))}
        </div>

        <Button size="sm" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}
